import { RefObject, useLayoutEffect } from 'react';

// Sizes the recording edit side panel to the part of the main content column
// that is currently on screen.
//
// The panel sits next to that column and sticks to the top of the viewport
// (CSS), so the browser keeps the two aligned while the page scrolls. What CSS
// cannot express is the height: it has to stop at whichever comes first, the
// bottom of the column or the bottom of the viewport, or the last form fields
// end up below the fold on short screens with no way to scroll to them.

interface VerticalBounds {
  top: number;
  bottom: number;
}

// `column` is viewport-relative, as returned by getBoundingClientRect().
//
// There is deliberately no minimum: the panel cannot leave the column's track,
// so a panel taller than the visible part of the column gets pushed up and
// loses its header (Close / Full screen) off the top of the screen.
export function visiblePanelHeight(
  column: VerticalBounds,
  viewportHeight: number
): number {
  const top = Math.max(column.top, 0);
  const bottom = Math.min(column.bottom, viewportHeight);
  return Math.max(bottom - top, 0);
}

export function useEditPanelHeight(
  active: boolean,
  columnRef: RefObject<HTMLElement>,
  panelRef: RefObject<HTMLElement>
): void {
  // Layout effect so the panel never paints at its unmeasured height.
  useLayoutEffect(() => {
    const column = columnRef.current;
    const panel = panelRef.current;
    if (!active || !column || !panel) return;

    // Written straight to the element: this runs on every scroll event, and
    // going through state would re-render the whole discography page each time.
    const update = () => {
      panel.style.height = `${visiblePanelHeight(
        column.getBoundingClientRect(),
        window.innerHeight
      )}px`;
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(column);
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [active, columnRef, panelRef]);
}
